<template>
  <div data-test="form-learning-outcome">
    <div
      v-if="!editable"
      data-test="form-learning-outcome-read"
    >
      <div
        v-if="isFieldVisible('name')"
        class="mb-2"
      >
        <strong>{{ getFieldLabel('name') || 'Name' }}</strong>
        <div>{{ value.name || '-' }}</div>
      </div>
      <div
        v-if="isFieldVisible('tags')"
        class="mb-2"
      >
        <strong>{{ getFieldLabel('tags') || 'Tags' }}</strong>
        <div>
          <h5>
            <div
              v-for="(tag, index) in value.tags"
              :key="'badge_' + index"
              class="badge badge-secondary mr-1 mb-1"
            >
              {{ tag }}
            </div>
          </h5>
        </div>
        <div v-if="!(value.tags || []).length">-</div>
      </div>
      <div class="d-flex mb-2">
        <div
          v-if="isFieldVisible('activity')"
          class="flex-1"
        >
          <strong>{{ getFieldLabel('activity') || 'Activity' }}</strong>
          <div>{{ value.activity || '-' }}</div>
        </div>
        <div
          v-if="isFieldVisible('assessment')"
          class="flex-1"
        >
          <strong>{{ getFieldLabel('assessment') || 'Assessment' }}</strong>
          <div>{{ value.assessment || '-' }}</div>
        </div>
        <div
          v-if="isFieldVisible('objective')"
          class="flex-1"
        >
          <strong>{{ getFieldLabel('objective') || 'Objective' }}</strong>
          <div>{{ value.objective || '-' }}</div>
        </div>
      </div>
      <div
        v-if="isFieldVisible('justification')"
        class="d-flex"
      >
        <div class="flex-1">
          <strong>{{ getFieldLabel('justification') || 'Justification' }}</strong>
          <div>{{ value.justification || '-' }}</div>
        </div>
      </div>
    </div>
    <div v-show="editable">
      <DynamicForm
        :editable="editable"
        :formId="formId"
      />
      <AppButton
        v-if="!disabled"
        variant="danger"
        @click="$emit('delete')"
      >
        <AppIcon name="delete" />
      </AppButton>
    </div>
  </div>
</template>

<script>
import { ref, computed, toRefs } from '@vue/composition-api'
import useDynamicForm from '@/components/DynamicForms/useDynamicForm'
import { userHasAnyAllowedRole } from '@shared/permissions/validators'
import useDataSources from '@/components/DynamicForms/useDataSources'
import bugsnagClient from '@/scripts/bugsnag.js'

export default {
  components: {
    DynamicForm: () => import('@/components/DynamicForms/DynamicForm'),
  },
  props: {
    value: {
      required: true,
      type: Object,
    },
    index: {
      type: Number,
      required: true,
    },
    formValue: {
      type: Object,
      default: () => ({}),
    },
    config: {
      required: true,
      type: Object,
    },
    editable: {
      default: true,
      type: Boolean,
    },
    disabled: {
      default: false,
      type: Boolean,
    },
    iteration: {
      type: Number,
      default: 0,
    },
    nestedMode: {
      type: Boolean,
      default: true,
    },
  },
  setup (props, { root }) {
    const { config, formValue, value } = toRefs(props)
    const path = computed(() => `learningOutcomes.${props.index}`)

    const { addLocalSourceData } = useDataSources({ overrideLocalSources: true })
    addLocalSourceData('record', value)

    const user = root.$store.getters.user

    const fieldsWithPermissions = computed(() => Object.values((config.value && config.value.fields) || {}))
    const questions = ref(fieldsWithPermissions.value.map(it => ({ ...it, questionId: it.dataKey })))
    const templateLayout = [
      [ 'name', 'tags' ],
      [ 'activity', 'assessment' ],
      [ 'objective' ],
      [ 'justification' ],
    ]

    function isFieldVisible (fieldKey) {
      const question = questions.value.find(q => q.questionId === fieldKey || q.dataKey === fieldKey)
      if (!question) {
        bugsnagClient.notify(`FormLearningOutcome: Question maching "${fieldKey}" not found! User: "${user.email}" - School: "${user.affiliation.school}"`)
        return false // To avoid throwing user error
      }
      return userHasAnyAllowedRole(user, null, question.rolesAllowedToSee)
    }
    function generateTemplateRow (rowKeys, index) {
      return {
        type: 'row',
        id: `outcome_row_${index}`,
        children: rowKeys.map(fieldKey => ({ type: 'question', id: fieldKey })),
      }
    }
    const template = ref(templateLayout.map(generateTemplateRow))

    const { formId } = useDynamicForm({
      questions,
      template,
      formValue: props.nestedMode ? formValue : value,
      path: props.nestedMode ? path : undefined,
    })

    function getFieldLabel (fieldKey) {
      return questions.value.find(q => q.questionId === fieldKey)?.label
    }

    return {
      formId,
      isFieldVisible,
      getFieldLabel,
    }
  },
}
</script>
